import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Upload, X, AlertCircle, FileText, Save, Eye } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { createBlog, updateBlog, getBlogById, uploadBlogImage, CreateBlogRequest, UpdateBlogRequest, BlogStatus } from '../../services/blogApi';
import { useRbac } from '../../context';
import { isRichTextEmpty, toEditorContent } from '../../utils/richText';

const blogEditorModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const blogEditorFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
];

const BLOG_IMAGE_MAX_SIZE_BYTES = 10 * 1024 * 1024;
const BLOG_IMAGE_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const validateBlogImageFile = (file: File): string | null => {
  if (!BLOG_IMAGE_ALLOWED_TYPES.includes(file.type)) {
    return 'Only JPEG, PNG, GIF, and WebP images are allowed.';
  }
  if (file.size > BLOG_IMAGE_MAX_SIZE_BYTES) {
    return 'Image must be 10 MB or smaller.';
  }
  return null;
};

export default function BlogForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useRbac();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<CreateBlogRequest>({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    status: 'draft',
    tags: [],
    seoTitle: '',
    seoDescription: '',
    seoKeywords: []
  });

  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string>('');

  // Check if user is admin
  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      toast.error('Access denied. Admin privileges required.');
      navigate('/admin/blogs');
    }
  }, [user, navigate]);

  // Fetch blog data if editing
  useEffect(() => {
    if (isEditMode && id) {
      setIsLoading(true);
      getBlogById(id)
        .then((response) => {
          if (response.success && response.data.blog) {
            const blog = response.data.blog;
            setFormData({
              title: blog.title,
              content: toEditorContent(blog.content),
              excerpt: blog.excerpt || '',
              featuredImage: blog.featuredImage || '',
              status: blog.status,
              tags: blog.tags || [],
              seoTitle: blog.seoTitle || '',
              seoDescription: blog.seoDescription || '',
              seoKeywords: blog.seoKeywords || []
            });
            if (blog.featuredImage) {
              setImagePreview(blog.featuredImage);
            }
          } else {
            toast.error(response.message || 'Blog not found or could not be loaded');
            navigate('/admin/blogs');
          }
        })
        .catch((error: unknown) => {
          console.error('Error fetching blog:', error);
          const message = error instanceof Error ? error.message : 'Failed to load blog data';
          toast.error(message);
          navigate('/admin/blogs');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEditMode, navigate]);

  const handleInputChange = (field: keyof CreateBlogRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageUpload = async (file: File) => {
    const validationError = validateBlogImageFile(file);
    if (validationError) {
      toast.error(validationError, { id: 'image-upload' });
      return;
    }

    try {
      toast.loading('Uploading image...', { id: 'image-upload' });
      const response = await uploadBlogImage(file);

      if (response.success && response.data.url) {
        const imageUrl = response.data.url;
        setFormData(prev => ({ ...prev, featuredImage: imageUrl }));
        setImagePreview(imageUrl);
        toast.success('Image uploaded successfully!', { id: 'image-upload' });
      } else {
        toast.error('Upload failed. No image URL was returned.', { id: 'image-upload' });
      }
    } catch (error: unknown) {
      console.error('Image upload error:', error);
      const message = error instanceof Error ? error.message : 'Failed to upload image';
      toast.error(message, { id: 'image-upload' });
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddKeyword = () => {
    const keyword = keywordInput.trim().toLowerCase();
    if (keyword && !formData.seoKeywords?.includes(keyword)) {
      setFormData(prev => ({
        ...prev,
        seoKeywords: [...(prev.seoKeywords || []), keyword]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      seoKeywords: (prev.seoKeywords || []).filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (isRichTextEmpty(formData.content)) {
      newErrors.content = 'Content is required';
    }
    if (formData.excerpt && formData.excerpt.length > 500) {
      newErrors.excerpt = 'Excerpt cannot exceed 500 characters';
    }
    if (formData.seoTitle && formData.seoTitle.length > 60) {
      newErrors.seoTitle = 'SEO title cannot exceed 60 characters';
    }
    if (formData.seoDescription && formData.seoDescription.length > 160) {
      newErrors.seoDescription = 'SEO description cannot exceed 160 characters';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent, publish: boolean = false) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      toast.error(Object.values(validationErrors)[0]);
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        status: publish ? 'published' : formData.status
      };

      if (isEditMode && id) {
        const response = await updateBlog(id, submitData as UpdateBlogRequest);
        if (response.success) {
          toast.success(publish ? 'Blog published successfully!' : 'Blog updated successfully!');
          navigate('/admin/blogs');
        } else {
          toast.error(response.message || 'Failed to update blog');
        }
      } else {
        const response = await createBlog(submitData);
        if (response.success) {
          toast.success(publish ? 'Blog published successfully!' : 'Blog saved as draft!');
          navigate('/admin/blogs');
        } else {
          toast.error(response.message || 'Failed to save blog');
        }
      }
    } catch (error: unknown) {
      console.error('Submit error:', error);
      const message = error instanceof Error ? error.message : 'Failed to save blog';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading blog data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/blogs')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditMode ? 'Edit Blog' : 'Create New Blog'}
            </h1>
            <p className="text-gray-500 mt-1">
              {isEditMode ? 'Update your blog post' : 'Write and publish a new blog post'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Blog Content</CardTitle>
                <CardDescription>Write your blog post content</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter blog title"
                    className={`mt-2 ${errors.title ? 'border-red-500' : ''}`}
                    maxLength={200}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Excerpt */}
                <div>
                  <Label htmlFor="excerpt">Excerpt</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => handleInputChange('excerpt', e.target.value)}
                    placeholder="Short summary of your blog post (optional)"
                    className={`mt-2 ${errors.excerpt ? 'border-red-500' : ''}`}
                    rows={3}
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.excerpt?.length || 0}/500 characters
                  </p>
                  {errors.excerpt && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.excerpt}
                    </p>
                  )}
                </div>

                {/* Content */}
                <div>
                  <Label htmlFor="content">Content *</Label>
                  <div
                    className={`mt-2 rounded-md border bg-white [&_.ql-container]:min-h-[320px] [&_.ql-editor]:min-h-[300px] [&_.ql-toolbar]:rounded-t-md [&_.ql-container]:rounded-b-md [&_.ql-picker-options]:z-50 ${
                      errors.content ? 'border-red-500' : 'border-input'
                    }`}
                  >
                    <ReactQuill
                      id="content"
                      theme="snow"
                      value={formData.content}
                      onChange={(value) => handleInputChange('content', value)}
                      placeholder="Write your blog content here..."
                      modules={blogEditorModules}
                      formats={blogEditorFormats}
                    />
                  </div>
                  {errors.content && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.content}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SEO Settings */}
            <Card>
              <CardHeader>
                <CardTitle>SEO Settings</CardTitle>
                <CardDescription>Optimize your blog for search engines</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="seoTitle">SEO Title</Label>
                  <Input
                    id="seoTitle"
                    value={formData.seoTitle}
                    onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                    placeholder="SEO optimized title (optional)"
                    className={`mt-2 ${errors.seoTitle ? 'border-red-500' : ''}`}
                    maxLength={60}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.seoTitle?.length || 0}/60 characters
                  </p>
                  {errors.seoTitle && (
                    <p className="text-red-500 text-sm mt-1">{errors.seoTitle}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="seoDescription">SEO Description</Label>
                  <Textarea
                    id="seoDescription"
                    value={formData.seoDescription}
                    onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                    placeholder="SEO meta description (optional)"
                    className={`mt-2 ${errors.seoDescription ? 'border-red-500' : ''}`}
                    rows={3}
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.seoDescription?.length || 0}/160 characters
                  </p>
                  {errors.seoDescription && (
                    <p className="text-red-500 text-sm mt-1">{errors.seoDescription}</p>
                  )}
                </div>

                <div>
                  <Label>SEO Keywords</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      placeholder="Add SEO keyword"
                    />
                    <Button type="button" onClick={handleAddKeyword} variant="outline">
                      Add
                    </Button>
                  </div>
                  {formData.seoKeywords && formData.seoKeywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.seoKeywords.map((keyword) => (
                        <Badge key={keyword} variant="secondary" className="flex items-center gap-1">
                          {keyword}
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(keyword)}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Publish Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleInputChange('status', value as BlogStatus)}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Update & Publish' : 'Publish'}
                  </Button>
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Saving...' : 'Save Draft'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Featured Image */}
            <Card>
              <CardHeader>
                <CardTitle>Featured Image</CardTitle>
                <CardDescription>JPEG, PNG, GIF, or WebP — max 10 MB</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Featured"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setImagePreview('');
                        handleInputChange('featuredImage', '');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-sm text-gray-500 mb-4">No featured image</p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUpload(file);
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="image-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('image-upload')?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
                <CardDescription>Add tags to categorize your blog</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add tag"
                  />
                  <Button type="button" onClick={handleAddTag} variant="outline">
                    Add
                  </Button>
                </div>
                {formData.tags && formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
